import React, { useState, useEffect } from 'react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Shield, GitPullRequest, FlaskConical, RefreshCw, Plus, Trash2,
  Settings, Eye, AlertTriangle, CheckCircle2, XCircle, ClipboardList,
  ChevronRight, ChevronDown, User, Loader2, ExternalLink, Package, FilterX,
} from 'lucide-react'
import { guardianService } from '@/services/guardian'

const actionConfig: Record<string, { icon: typeof Shield; color: string; bgColor: string; label: string }> = {
  'app.added':              { icon: Plus, color: 'text-blue-600', bgColor: 'bg-blue-50', label: 'App Added' },
  'app.removed':            { icon: Trash2, color: 'text-red-600', bgColor: 'bg-red-50', label: 'App Removed' },
  'app.settings_changed':   { icon: Settings, color: 'text-slate-600', bgColor: 'bg-slate-50', label: 'Settings Changed' },
  'scan.triggered':         { icon: RefreshCw, color: 'text-blue-600', bgColor: 'bg-blue-50', label: 'Scan Triggered' },
  'scan.triggered_all':     { icon: RefreshCw, color: 'text-blue-600', bgColor: 'bg-blue-50', label: 'Scan All' },
  'scan.triggered_selected': { icon: RefreshCw, color: 'text-blue-600', bgColor: 'bg-blue-50', label: 'Scan Selected' },
  'scan.completed':         { icon: CheckCircle2, color: 'text-emerald-600', bgColor: 'bg-emerald-50', label: 'Scan Completed' },
  'qa.triggered':           { icon: FlaskConical, color: 'text-violet-600', bgColor: 'bg-violet-50', label: 'QA Triggered' },
  'qa.bundle_triggered':    { icon: FlaskConical, color: 'text-violet-600', bgColor: 'bg-violet-50', label: 'Bundle QA' },
  'qa.completed':           { icon: FlaskConical, color: 'text-emerald-600', bgColor: 'bg-emerald-50', label: 'QA Completed' },
  'pr.triggered':           { icon: GitPullRequest, color: 'text-indigo-600', bgColor: 'bg-indigo-50', label: 'PR Triggered' },
  'pr.created':             { icon: GitPullRequest, color: 'text-emerald-600', bgColor: 'bg-emerald-50', label: 'PR Created' },
  'pr.merged':              { icon: CheckCircle2, color: 'text-emerald-600', bgColor: 'bg-emerald-50', label: 'PR Merged' },
  'pr.declined':            { icon: XCircle, color: 'text-red-600', bgColor: 'bg-red-50', label: 'PR Declined' },
  'settings.updated':       { icon: Settings, color: 'text-slate-600', bgColor: 'bg-slate-50', label: 'Settings Updated' },
  'llm.analysis_completed': { icon: Eye, color: 'text-amber-600', bgColor: 'bg-amber-50', label: 'AI Analysis' },
}
const defaultAction = { icon: AlertTriangle, color: 'text-slate-500', bgColor: 'bg-slate-50', label: 'Action' }

const actionGroups = [
  { label: 'Scans', types: ['scan.triggered', 'scan.triggered_all', 'scan.triggered_selected', 'scan.completed'] },
  { label: 'QA', types: ['qa.triggered', 'qa.bundle_triggered', 'qa.completed'] },
  { label: 'PRs', types: ['pr.triggered', 'pr.created', 'pr.merged', 'pr.declined'] },
  { label: 'Apps', types: ['app.added', 'app.removed', 'app.settings_changed'] },
  { label: 'AI', types: ['llm.analysis_completed'] },
  { label: 'Settings', types: ['settings.updated'] },
]

function eventSummary(e: any): string {
  const d = e.details || {}
  switch (e.action_type) {
    case 'scan.completed': return `${d.total_deps || 0} deps, ${d.vulnerabilities || 0} vulns, ${d.outdated || 0} outdated`
    case 'pr.created': return `PR with ${d.updates?.length || '?'} updates`
    case 'qa.completed': return `Verdict: ${d.verdict} (${d.updates_applied || 0} updates)`
    case 'llm.analysis_completed': return `${d.total_changes || 0} adjustments, ${d.summaries || 0} summaries`
    case 'scan.triggered_selected': return `${d.app_count || 0} app(s): ${(d.app_slugs || []).join(', ')}`
    case 'qa.bundle_triggered': return `${d.finding_ids_count || 0} findings selected`
    case 'app.added': return `${d.app_name || d.app_slug} (${d.framework})`
    case 'app.settings_changed': {
      const p: string[] = []
      if (d.scan_frequency) p.push(`frequency: ${d.scan_frequency}`)
      if (d.auto_merge_enabled !== undefined) p.push(`auto-merge: ${d.auto_merge_enabled ? 'on' : 'off'}`)
      return p.join(', ')
    }
    case 'pr.merged': return `${(d.findings_resolved || []).length} findings resolved`
    case 'pr.declined': return `${(d.findings_reopened || []).length} findings reopened`
    case 'settings.updated': {
      const changes: string[] = []
      if (d.scan_frequency) changes.push(`scan frequency: ${d.scan_frequency}`)
      if (d.scan_time_utc) changes.push(`scan time: ${d.scan_time_utc}`)
      if (d.auto_merge_enabled !== undefined) changes.push(`auto-merge: ${d.auto_merge_enabled ? 'on' : 'off'}`)
      if (d.auto_merge_policy) changes.push(`merge policy: ${d.auto_merge_policy.replace(/_/g, ' ')}`)
      return changes.length > 0 ? changes.join(', ') : 'Portfolio settings changed'
    }
    case 'scan.triggered': return d.app_slug || ''
    case 'app.removed': return d.app_slug || ''
    default: {
      // Show any details as a fallback summary
      const keys = Object.keys(d).filter(k => !['task_id', 'app_id'].includes(k))
      if (keys.length === 0) return ''
      return keys.map(k => {
        const v = d[k]
        return `${k.replace(/_/g, ' ')}: ${typeof v === 'boolean' ? (v ? 'on' : 'off') : v}`
      }).join(', ')
    }
  }
}

function runTitle(trigger: any): string {
  switch (trigger.action_type) {
    case 'scan.triggered_all': return 'Full Portfolio Scan'
    case 'scan.triggered_selected': return 'Scan Selected Apps'
    case 'scan.triggered': return 'App Scan'
    case 'qa.bundle_triggered': return 'QA Validation'
    case 'pr.triggered': return 'PR Creation'
    default: return actionConfig[trigger.action_type]?.label || 'Workflow'
  }
}

function runIcon(trigger: any) {
  if (trigger.action_type.startsWith('scan.')) return RefreshCw
  if (trigger.action_type.startsWith('qa.')) return FlaskConical
  if (trigger.action_type.startsWith('pr.')) return GitPullRequest
  return actionConfig[trigger.action_type]?.icon || Shield
}

// Detail types that have rich expandable content
const DETAIL_TYPES = new Set(['scan.completed', 'qa.completed', 'pr.created', 'llm.analysis_completed'])

function EventDetailPanel({ entryId }: { entryId: number }) {
  const [detail, setDetail] = useState<any>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    guardianService.getAuditDetail(entryId)
      .then(setDetail)
      .catch(() => setDetail(null))
      .finally(() => setLoading(false))
  }, [entryId])

  if (loading) return <div className="flex items-center gap-2 py-3 text-slate-400 text-xs"><Loader2 className="h-3 w-3 animate-spin" />Loading...</div>
  if (!detail) return null

  const d = detail.details || {}

  return (
    <div className="space-y-3 py-2">
      {/* Scan completed — findings + deps */}
      {detail.action_type === 'scan.completed' && (
        <>
          {(!detail.findings || detail.findings.length === 0) && (detail.details?.outdated > 0 || detail.details?.vulnerabilities > 0) && (
            <p className="text-[11px] text-slate-500">All findings from this scan are already being tracked from previous scans.</p>
          )}
          {detail.findings?.length > 0 && (
            <div>
              <p className="text-[11px] font-medium text-slate-600 mb-1">
                Current Findings ({detail.findings.filter((f: any) => f.finding_status === 'open').length} open
                {detail.findings.some((f: any) => f.finding_status === 'in_progress') && `, ${detail.findings.filter((f: any) => f.finding_status === 'in_progress').length} in PR`}
                {detail.findings.some((f: any) => f.finding_status === 'dismissed') && `, ${detail.findings.filter((f: any) => f.finding_status === 'dismissed').length} dismissed by AI`}
                )
              </p>
              <div className="space-y-0.5 max-h-40 overflow-y-auto">
                {detail.findings.map((f: any) => (
                  <div key={f.id} className="flex items-center gap-2 text-[11px] py-0.5">
                    <Badge variant="secondary" className={`text-[9px] px-1 border-0 ${
                      f.severity === 'critical' || f.severity === 'high' ? 'bg-red-100 text-red-700' :
                      f.severity === 'medium' ? 'bg-amber-100 text-amber-700' :
                      f.severity === 'low' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-500'
                    }`}>{f.severity}</Badge>
                    <span className="text-slate-600 truncate flex-1">{f.title}</span>
                    {f.cve_id && (
                      <a href={f.cve_id.startsWith('GHSA-') ? `https://github.com/advisories/${f.cve_id}` : `https://nvd.nist.gov/vuln/detail/${f.cve_id}`}
                        target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}
                        className="text-red-500 hover:underline flex items-center gap-0.5 shrink-0 text-[10px]">
                        {f.cve_id}<ExternalLink className="h-2 w-2" />
                      </a>
                    )}
                    <span className={`text-[9px] shrink-0 px-1.5 py-0.5 rounded ${
                      f.finding_status === 'dismissed' ? 'bg-slate-100 text-slate-400' :
                      f.finding_status === 'in_progress' ? 'bg-emerald-50 text-emerald-600' : ''
                    }`}>
                      {f.finding_status === 'dismissed' ? 'False positive (AI)' :
                       f.finding_status === 'in_progress' ? 'PR open' : ''}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {detail.dependencies?.length > 0 && (
            <div>
              <p className="text-[11px] font-medium text-slate-600 mb-0.5">Dependencies ({detail.dependencies.length})</p>
              <p className="text-[10px] text-slate-400 mb-1"><span className="text-emerald-600">Green arrow</span> = newer version available. Only packages 1+ major versions behind or with known vulnerabilities are raised as findings. Minor and patch updates are safe to defer.</p>
              <div className="grid grid-cols-2 gap-x-4 gap-y-0 max-h-32 overflow-y-auto">
                {detail.dependencies.map((dep: any, i: number) => (
                  <div key={i} className="flex items-center gap-1 text-[10px] py-0.5">
                    <Package className="h-2.5 w-2.5 text-slate-300 shrink-0" />
                    <span className="text-slate-600 font-mono truncate">{dep.package_name}</span>
                    <span className="text-slate-400">{dep.current_version}</span>
                    {dep.latest_version && dep.latest_version !== dep.current_version && (
                      <span className="text-emerald-600">-&gt; {dep.latest_version}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* QA completed — checks */}
      {detail.action_type === 'qa.completed' && detail.qa_checks && (
        <div>
          <p className="text-[11px] font-medium text-slate-600 mb-1">QA Checks</p>
          {detail.qa_checks.map((c: any, i: number) => (
            <div key={i} className="flex items-center gap-2 text-[11px] py-0.5">
              {c.check_status === 'pass' ? <CheckCircle2 className="h-3 w-3 text-emerald-500" /> :
               c.check_status === 'fail' ? <XCircle className="h-3 w-3 text-red-500" /> :
               c.check_status === 'warn' ? <AlertTriangle className="h-3 w-3 text-amber-500" /> :
               <div className="h-3 w-3 rounded-full bg-slate-200" />}
              <span className="font-medium text-slate-700 w-20">{c.check_name}</span>
              <span className="text-slate-500 flex-1 truncate">{c.output_summary}</span>
              {c.duration_ms > 0 && <span className="text-slate-400 text-[9px]">{(c.duration_ms / 1000).toFixed(1)}s</span>}
            </div>
          ))}
        </div>
      )}

      {/* PR created — link + updates */}
      {detail.action_type === 'pr.created' && (
        <div className="space-y-1.5">
          {d.pr_url && (
            <a href={d.pr_url} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}
              className="inline-flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-800 font-medium">
              <GitPullRequest className="h-3.5 w-3.5" /> View Pull Request <ExternalLink className="h-2.5 w-2.5" />
            </a>
          )}
          {d.updates && (
            <div>
              <p className="text-[11px] font-medium text-slate-600 mb-0.5">Updates ({d.updates.length})</p>
              <ul className="text-[10px] text-slate-500 font-mono space-y-0">
                {d.updates.map((u: string, i: number) => <li key={i}>- {u}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* LLM analysis — portfolio summary */}
      {detail.action_type === 'llm.analysis_completed' && detail.analysis && (
        <div className="space-y-2">
          {detail.analysis.portfolio_context && (
            <div>
              <div className="flex items-center gap-2 mb-1">
                <p className="text-[11px] font-medium text-slate-600">Portfolio Assessment</p>
                <Badge variant="secondary" className="text-[9px] px-1">Risk: {detail.analysis.portfolio_context.portfolio_risk_level}</Badge>
              </div>
              <p className="text-[11px] text-slate-500">{detail.analysis.portfolio_context.portfolio_summary}</p>
            </div>
          )}
          {detail.analysis.app_summaries && Object.entries(detail.analysis.app_summaries).map(([slug, data]: [string, any]) => (
            <div key={slug} className="border-l-2 border-slate-200 pl-2">
              <p className="text-[11px] font-medium text-slate-700">{slug}</p>
              <p className="text-[10px] text-slate-500">{data.summary}</p>
              {data.action_items?.length > 0 && (
                <ul className="text-[10px] text-slate-500 mt-0.5">
                  {data.action_items.map((item: string, i: number) => <li key={i}>- {item}</li>)}
                </ul>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function EventRow({ entry, isChild, expandedEventId, onToggle }: { entry: any; isChild?: boolean; expandedEventId: number | null; onToggle: (id: number) => void }) {
  const config = actionConfig[entry.action_type] || defaultAction
  const Icon = config.icon
  const summary = eventSummary(entry)
  const d = entry.details || {}
  const hasDetail = DETAIL_TYPES.has(entry.action_type)
  const isExpanded = expandedEventId === entry.id

  return (
    <div className={isChild ? 'pl-8' : ''}>
      <div
        className={`flex items-start gap-3 py-2 ${hasDetail ? 'cursor-pointer hover:bg-slate-100/50 -mx-2 px-2 rounded' : ''}`}
        onClick={() => hasDetail && onToggle(entry.id)}
      >
        {hasDetail ? (
          isExpanded ? <ChevronDown className="h-3.5 w-3.5 text-slate-400 mt-1 shrink-0" /> : <ChevronRight className="h-3.5 w-3.5 text-slate-400 mt-1 shrink-0" />
        ) : (
          <div className="w-3.5 shrink-0" />
        )}
        <div className={`h-6 w-6 rounded-md ${config.bgColor} flex items-center justify-center shrink-0 mt-0.5`}>
          <Icon className={`h-3.5 w-3.5 ${config.color}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-slate-800">{config.label}</span>
            {entry.app_name && <span className="text-[10px] text-slate-400">{entry.app_name}</span>}
          </div>
          {summary && <p className="text-[11px] text-slate-500 mt-0.5">{summary}</p>}
          {entry.action_type === 'pr.created' && d.pr_url && (
            <a href={d.pr_url} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}
              className="inline-flex items-center gap-1 text-[11px] text-blue-600 hover:underline mt-0.5">
              View PR <ExternalLink className="h-2.5 w-2.5" />
            </a>
          )}
        </div>
        <div className="text-right shrink-0">
          <span className="text-[10px] text-slate-400">
            {new Date(entry.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
          </span>
        </div>
      </div>
      {isExpanded && (
        <div className="ml-10 mr-2 mb-2 px-3 py-2 bg-white rounded-lg border border-slate-200">
          <EventDetailPanel entryId={entry.id} />
        </div>
      )}
    </div>
  )
}

// Workflow types that start a correlated group
const WORKFLOW_TRIGGERS = new Set(['scan.triggered_all', 'scan.triggered_selected', 'scan.triggered', 'qa.bundle_triggered', 'pr.triggered'])

export function GuardianAuditTab() {
  const [entries, setEntries] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [expandedRun, setExpandedRun] = useState<string | null>(null)
  const [expandedEventId, setExpandedEventId] = useState<number | null>(null)
  const [filterAction, setFilterAction] = useState('all')
  const [filterActor, setFilterActor] = useState('all')
  const [filterApp, setFilterApp] = useState('all')
  const [filterDate, setFilterDate] = useState('')
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(10)

  useEffect(() => { loadAudit() }, [])
  useEffect(() => { setPage(0) }, [filterAction, filterActor, filterApp, filterDate, pageSize])

  async function loadAudit() {
    setLoading(true)
    try { setEntries(await guardianService.getAuditLog(500)) }
    catch (e) { console.error('Failed to load audit log:', e) }
    finally { setLoading(false) }
  }

  if (loading) return <div className="space-y-3">{[1,2,3,4,5].map(i => <Skeleton key={i} className="h-12 rounded-lg" />)}</div>

  if (entries.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center space-y-4">
        <div className="mx-auto h-20 w-20 rounded-full bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center">
          <ClipboardList className="h-10 w-10 text-slate-400" />
        </div>
        <h3 className="text-xl font-semibold text-slate-900">No audit history yet</h3>
        <p className="text-slate-500 max-w-sm mx-auto">All Guardian actions will be logged here for full traceability.</p>
      </div>
    )
  }

  // Build workflow runs: group correlated events, standalone events are solo
  type Run = { id: string; trigger: any; events: any[]; date: string }
  const runs: Run[] = []
  const correlationMap = new Map<string, Run>()
  const standalone: any[] = []

  for (const e of entries) {
    if (e.correlation_id) {
      let run = correlationMap.get(e.correlation_id)
      if (!run) {
        run = {
          id: e.correlation_id,
          trigger: e,
          events: [],
          date: new Date(e.created_at).toISOString().split('T')[0],
        }
        correlationMap.set(e.correlation_id, run)
        runs.push(run)
      }
      // The trigger is the first event (earliest), children are the rest
      if (WORKFLOW_TRIGGERS.has(e.action_type) && new Date(e.created_at) <= new Date(run.trigger.created_at)) {
        run.trigger = e
      }
      run.events.push(e)
    } else {
      standalone.push(e)
    }
  }

  // Merge standalone events as single-event runs
  for (const e of standalone) {
    runs.push({ id: `s-${e.id}`, trigger: e, events: [e], date: new Date(e.created_at).toISOString().split('T')[0] })
  }

  // Sort runs by most recent event
  runs.sort((a, b) => {
    const aTime = Math.max(...a.events.map((e: any) => new Date(e.created_at).getTime()))
    const bTime = Math.max(...b.events.map((e: any) => new Date(e.created_at).getTime()))
    return bTime - aTime
  })

  // Sort events within each run chronologically
  for (const run of runs) {
    run.events.sort((a: any, b: any) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
  }

  // Filters
  const uniqueActors = [...new Set(entries.map(e => e.actor || 'system'))].sort()
  const uniqueApps = [...new Set(entries.filter(e => e.app_name).map(e => e.app_name))].sort()

  const filteredRuns = runs.filter(run => {
    const matchesAction = filterAction === 'all' || run.events.some((e: any) => {
      const group = actionGroups.find(g => g.label === filterAction)
      return group ? group.types.includes(e.action_type) : e.action_type === filterAction
    })
    const matchesActor = filterActor === 'all' || run.events.some((e: any) => (e.actor || 'system') === filterActor)
    const matchesApp = filterApp === 'all' || run.events.some((e: any) => e.app_name === filterApp)
    const matchesDate = !filterDate || run.date === filterDate
    return matchesAction && matchesActor && matchesApp && matchesDate
  })

  const hasFilters = filterAction !== 'all' || filterActor !== 'all' || filterApp !== 'all' || filterDate !== ''
  const totalPages = Math.ceil(filteredRuns.length / pageSize)
  const paginatedRuns = filteredRuns.slice(page * pageSize, (page + 1) * pageSize)

  // Group by date
  const grouped: Record<string, Run[]> = {}
  for (const run of paginatedRuns) {
    const dateLabel = new Date(run.trigger.created_at).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })
    if (!grouped[dateLabel]) grouped[dateLabel] = []
    grouped[dateLabel].push(run)
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        <Select value={filterAction} onValueChange={setFilterAction}>
          <SelectTrigger className="w-[130px] h-8 text-xs"><SelectValue placeholder="All actions" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All actions</SelectItem>
            {actionGroups.map(g => <SelectItem key={g.label} value={g.label}>{g.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterActor} onValueChange={setFilterActor}>
          <SelectTrigger className="w-[170px] h-8 text-xs"><SelectValue placeholder="All actors" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All actors</SelectItem>
            {uniqueActors.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterApp} onValueChange={setFilterApp}>
          <SelectTrigger className="w-[170px] h-8 text-xs"><SelectValue placeholder="All apps" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All apps</SelectItem>
            {uniqueApps.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input type="date" className="w-[150px] h-8 text-xs" value={filterDate} onChange={e => setFilterDate(e.target.value)} />
        {hasFilters && (
          <Button variant="ghost" size="sm" className="h-8 gap-1.5 text-xs text-slate-500"
            onClick={() => { setFilterAction('all'); setFilterActor('all'); setFilterApp('all'); setFilterDate('') }}>
            <FilterX className="h-3.5 w-3.5" /> Clear
          </Button>
        )}
        <span className="text-[11px] text-slate-400 ml-auto">{filteredRuns.length} workflow{filteredRuns.length !== 1 ? 's' : ''}</span>
      </div>

      {filteredRuns.length === 0 ? (
        <p className="text-center py-8 text-sm text-slate-400">No events match the selected filters.</p>
      ) : (
        <div className="space-y-6">
          {Object.entries(grouped).map(([date, dateRuns]) => (
            <div key={date}>
              <div className="sticky top-0 bg-white/95 backdrop-blur-sm z-10 py-2 border-b mb-2">
                <h3 className="text-sm font-semibold text-slate-900">{date}</h3>
              </div>

              <div className="space-y-2">
                {dateRuns.map(run => {
                  const isExpanded = expandedRun === run.id
                  const isSingle = run.events.length === 1
                  const RunIcon = runIcon(run.trigger)
                  const childEvents = run.events.filter(e => e.id !== run.trigger.id)
                  const apps = [...new Set(run.events.filter((e: any) => e.app_name).map((e: any) => e.app_name))]

                  return (
                    <div key={run.id} className={`rounded-lg border transition-colors ${isExpanded ? 'bg-slate-50 border-slate-200' : 'bg-white hover:border-slate-300'}`}>
                      {/* Run header */}
                      <div
                        className="flex items-center gap-3 px-4 py-3 cursor-pointer"
                        onClick={() => !isSingle && setExpandedRun(isExpanded ? null : run.id)}
                      >
                        {!isSingle ? (
                          isExpanded ? <ChevronDown className="h-4 w-4 text-slate-400 shrink-0" /> : <ChevronRight className="h-4 w-4 text-slate-400 shrink-0" />
                        ) : (
                          <div className="w-4" />
                        )}

                        <div className={`h-8 w-8 rounded-lg ${actionConfig[run.trigger.action_type]?.bgColor || 'bg-slate-50'} flex items-center justify-center shrink-0`}>
                          <RunIcon className={`h-4 w-4 ${actionConfig[run.trigger.action_type]?.color || 'text-slate-500'}`} />
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-slate-900">{runTitle(run.trigger)}</span>
                            {apps.map(app => (
                              <Badge key={app} variant="outline" className="text-[10px] px-1.5">{app}</Badge>
                            ))}
                            {!isSingle && (
                              <span className="text-[10px] text-slate-400">{run.events.length} events</span>
                            )}
                          </div>
                          {isSingle && <p className="text-xs text-slate-500 mt-0.5">{eventSummary(run.trigger)}</p>}
                        </div>

                        <div className="text-right shrink-0">
                          <p className="text-[11px] text-slate-400">
                            {new Date(run.trigger.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </p>
                          <div className="flex items-center gap-1 justify-end mt-0.5">
                            <User className="h-2.5 w-2.5 text-slate-300" />
                            <span className="text-[10px] text-slate-400">{run.trigger.actor || 'system'}</span>
                          </div>
                        </div>
                      </div>

                      {/* Expanded timeline */}
                      {isExpanded && !isSingle && (
                        <div className="border-t px-4 py-2 space-y-0">
                          {run.events.map(event => (
                            <EventRow key={event.id} entry={event} isChild={event.id !== run.trigger.id}
                              expandedEventId={expandedEventId}
                              onToggle={(id) => setExpandedEventId(expandedEventId === id ? null : id)} />
                          ))}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      <div className="flex items-center justify-between pt-2">
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-400">Rows per page</span>
          <Select value={String(pageSize)} onValueChange={v => setPageSize(Number(v))}>
            <SelectTrigger className="w-[70px] h-7 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              {[5, 10, 25, 50].map(n => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
            </SelectContent>
          </Select>
          <span className="text-xs text-slate-400">Page {page + 1} of {totalPages || 1}</span>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="outline" size="sm" className="h-7 text-xs" disabled={page === 0} onClick={() => setPage(0)}>First</Button>
          <Button variant="outline" size="sm" className="h-7 text-xs" disabled={page === 0} onClick={() => setPage(p => p - 1)}>Previous</Button>
          <Button variant="outline" size="sm" className="h-7 text-xs" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>Next</Button>
          <Button variant="outline" size="sm" className="h-7 text-xs" disabled={page >= totalPages - 1} onClick={() => setPage(totalPages - 1)}>Last</Button>
        </div>
      </div>
    </div>
  )
}
